import { Container } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { initialGoUp, whileInView, transition } from '../../../helpers/animations';
import { getAboutCathayHackathonCardIndexes, useHomeTranslationsQuery } from '../../../services/HomePageServices';
import { useTranslation } from 'react-i18next';
import { FC } from 'react';
import parse from 'html-react-parser';
import { Nav } from 'react-bootstrap';
import { sanitizeCmsHtml } from '../../../helpers/sanitizeCmsHtml';

interface StatCardProps {
  statCardIndex: number;
}

const StatCard: FC<StatCardProps> = ({ statCardIndex }) => {
  const { t } = useTranslation('about_cathay_hackathon');
  return (
    <div className='about-hackathon-section__stat-card'>
      <div className='about-hackathon-section__stat-number'>{t(`about_recap_card_number_${statCardIndex}`)}</div>
      <div className='about-hackathon-section__stat-description'>{t(`about_recap_card_description_${statCardIndex}`)}</div>
    </div>
  );
};


function AboutCathayHackathon() {
  const { t } = useTranslation(['about_cathay_hackathon', 'general_site']);
  const homeQuery = useHomeTranslationsQuery();
  const recapCardIndexes = homeQuery.data ? getAboutCathayHackathonCardIndexes(homeQuery.data) : undefined;
  const isLoading = homeQuery.isLoading;
  const isError = homeQuery.isError;

  const aboutSectionName = t('about_section_name');
  const aboutTitle = t('about_title');
  const aboutDesktopKv = t('about_kv');
  const aboutDescription = t('about_description');
  const aboutBodyText = t('about_body_text');
  const aboutButtonText = t('about_button_text');
  const aboutButtonLink = t('about_button_link');
  const hasAboutSectionName = !!aboutSectionName && aboutSectionName !== 'about_section_name';
  const hasAboutTitle = !!aboutTitle && aboutTitle !== 'about_title';
  const hasAboutDesktopKv = !!aboutDesktopKv && aboutDesktopKv !== 'about_kv';
  const hasAboutDescription = !!aboutDescription && aboutDescription !== 'about_description';
  const hasAboutBodyText = !!aboutBodyText && aboutBodyText !== 'about_body_text';
  const hasAboutButtonText = !!aboutButtonText && aboutButtonText !== 'about_button_text';
  const hasAboutButtonLink = !!aboutButtonLink && aboutButtonLink !== 'about_button_link';

  if (isLoading) return null;

  if (isError || !recapCardIndexes) {
    return (
      <section id="about-hackathon" className="about-hackathon-section">
        <Container className="about-hackathon-section__container">
          <p className="about-hackathon-section__error" role="status">
            {t('loading_error_text', { ns: 'general_site',
              defaultValue: 'Content is temporarily unavailable. Please refresh and try again.' })}
          </p>
        </Container>
      </section>
    );
  }

  return (
    <section id="about-hackathon" className="about-hackathon-section">
      <Container className="about-hackathon-section__container">
        <motion.div
          initial={initialGoUp}
          whileInView={whileInView}
          viewport={{
            once: true,
            amount: 0.2
          }}
          transition={transition}
          className='about-hackathon-section__content'
        >
          <div className='about-hackathon-section__about'>
            {hasAboutSectionName ? (
              <div className='about-hackathon-section__section-name'>{aboutSectionName}</div>
            ) : null}
            {hasAboutTitle ? (
              <div className='about-hackathon-section__title'>{aboutTitle}</div>
            ) : null}
            {hasAboutDescription ? (
              <div className='about-hackathon-section__description'>{parse(sanitizeCmsHtml(aboutDescription))}</div>
            ) : null}
          </div>
          {hasAboutDesktopKv ? (
            <div className='about-hackathon-section__banner-wrapper'>
              <img
                className='about-hackathon-section__banner'
                src={aboutDesktopKv}
                alt={hasAboutTitle ? aboutTitle : 'About hackathon banner'}
                loading="lazy"
                decoding="async"
              />
            </div>
          ) : null}
        </motion.div>
        <motion.div
          initial={initialGoUp}
          whileInView={whileInView}
          viewport={{
            once: true,
            amount: 0.2
          }}
          transition={transition}
          className='about-hackathon-section__stats'
        >
          <div className='about-hackathon-section__stat-cards'>
            {recapCardIndexes.map((idx) => (
              <StatCard key={`stat_card_${idx}`} statCardIndex={idx} />
            ))}
          </div>
          {hasAboutBodyText ? (
            <div className='about-hackathon-section__body-text'>
              {parse(sanitizeCmsHtml(aboutBodyText))}
            </div>
          ) : null}
          {hasAboutButtonText && hasAboutButtonLink ? (
            <div className='about-hackathon-section__button-wrapper'>
              <Nav.Link className='about-hackathon-section__button' href={aboutButtonLink}>
                {aboutButtonText}
              </Nav.Link>
            </div>
          ) : null}
        </motion.div>
      </Container>
    </section>
  );
}

export default AboutCathayHackathon;
